<script setup lang="ts">
import { computed, h, onMounted, ref, watch, type VNode, type VNodeChild } from 'vue'
import { useRouter } from 'vue-router'
import AppCard from '@/components/ui/AppCard.vue'
import DenseTable from '@/components/ui/DenseTable.vue'
import DateField from '@/components/ui/DateField.vue'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import Select from 'primevue/select'
import Message from 'primevue/message'
import Paginator from 'primevue/paginator'
import Tag from 'primevue/tag'
import Accordion from 'primevue/accordion'
import AccordionTab from 'primevue/accordiontab'
import AppDialog from '@/components/ui/Dialog.vue'
import { useAuthStore } from '@/stores/auth'
import { useBreakpoint } from '@/composables/useBreakpoint'
import { useFiltrosEnUrl } from '@/composables/useFiltrosEnUrl'
import {
  quoteApi,
  QUOTE_STATUSES,
  type Advisor,
  type FollowUpFilter,
  type PriceType,
  type Quote,
  type QuoteStatus,
} from '@/services/quotes'
import { formatCurrency, formatShortDate } from '@/utils/format'
import { statusLabel, statusSeverity } from '@/utils/status'

/**
 * Listado de cotizaciones.
 *
 * Todos los filtros se aplican en el servidor. Filtrar en el cliente haría que la
 * cuenta de "cuántas hay" y la tabla de la pantalla discrepen, que es el peor
 * síntoma que puede tener un listado: el usuario ve "de 1 a 20 de 47" y solo
 * 4 filas.
 *
 * `loading`, `error` e `items` se guardan por separado a propósito: con un solo
 * booleano un fallo de red se pintaría como "no hay cotizaciones", que es un
 * diagnóstico falso y hace que el usuario repita acciones que ya funcionaban.
 */

const router = useRouter()
const authStore = useAuthStore()
const layout = useBreakpoint()

const isDesktop = computed(() => layout.value === 'desktop')
const canManage = computed(() => authStore.hasPermission('quotes.manage'))

const items = ref<Quote[]>([])
const advisors = ref<Advisor[]>([])
const loading = ref(false)
const errorMessage = ref<string | null>(null)

const search = ref('')
const status = ref<QuoteStatus | ''>('')
const priceType = ref<PriceType | ''>('')
const followUp = ref<FollowUpFilter | ''>('')
const advisorId = ref<number | ''>('')
const invoiced = ref<'' | 'si' | 'no'>('')
const attentionLineFilter = ref<string | ''>('')

/**
 * Rango de fechas de la cotización, en `YYYY-MM-DD`.
 *
 * Son dos campos y no uno porque el backend los espera separados —`date_from` y
 * `date_to`—, y porque una fecha suelta no responde a la pregunta que se hace al
 * filtrar: lo habitual no es «las del martes», sino «las de este mes» o «las de
 * este trimestre». Escribir un rango es la forma natural de decirlo.
 *
 * El backend ya acepta los dos y los valida (`after_or_equal:date_from`); aquí
 * faltaba la parte de poder elegirlos.
 */
const dateFrom = ref('')
const dateTo = ref('')

/** `''` sin filtro, `'si'` solo con anticipo, `'no'` solo sin él. */
const advanceFilter = ref<'' | 'si' | 'no'>('')
const page = ref(1)

/*
| Los filtros viven en la URL
|--------------------------------------------------------------------------
|
| Filtrabas, abrías una cotización, volvías, y el filtro había desaparecido. No era el
| botón de atrás: es que los filtros estaban en `ref`s de este componente, y un componente
| no sobrevive a que te vayas a otra vista. Al volver se crea otro, con los `ref` vacíos.
| El filtro no tenía ningún sitio donde quedarse.
 |
| Aquí sí lo tiene. Y con `conQuery` los enlaces a la ficha y el botón «Volver» se llevan
| los filtros encima, que es la otra mitad: «Volver» empuja una entrada **nueva**, así que
| el historial por sí solo no basta.
|
| Las listas de `permitidos` no son adorno: sin ellas `?estado=inventado` se aplicaría como
| filtro y la consulta saldría con un estado que el backend no entiende.
*/

const total = ref(0)
const perPage = 20

/**
 * Opciones del filtro de estado.
 *
 * Las etiquetas van en palabra, no en mayúsculas: dentro de un desplegable una
 * mayúscula es ruido, y `ENVIADA` no dice más que `Enviada`. Las mayúsculas son para
 * el `Tag`, que es donde el estado se lee de un vistazo.
 */
const statusOptions: Array<{ label: string; value: QuoteStatus | '' }> = [
  { label: 'Todos los estados', value: '' },
  ...QUOTE_STATUSES.map((value) => ({ label: statusLabel(value).toLowerCase(), value })),
]

/**
 * Los estados que se pueden elegir **en la fila**.
 *
 * Es distinta de `statusOptions`, que es la lista del filtro y lleva «Todos los
 * estados» además. Filtrar por «Todos» no es un estado que pueda tener una cotización, y
 * en un desplegable de una fila sería una opción que no significa nada.
 */
const estadosDeFila: Array<{ label: string; value: QuoteStatus }> = QUOTE_STATUSES.map(
  (value) => ({ label: statusLabel(value), value }),
)

const priceTypeOptions: Array<{ label: string; value: PriceType | '' }> = [
  { label: 'Minorista y mayorista', value: '' },
  { label: 'Solo minorista', value: 'retail' },
  { label: 'Solo mayorista', value: 'wholesale' },
]

const followUpOptions: Array<{ label: string; value: FollowUpFilter | '' }> = [
  { label: 'Cualquier seguimiento', value: '' },
  { label: 'Vencidos', value: 'vencido' },
  { label: 'Para hoy', value: 'hoy' },
  { label: 'Próximos', value: 'proximo' },
  { label: 'Sin seguimiento', value: 'sin_seguimiento' },
  { label: 'Con notas nuevas', value: 'con_notas_nuevas' },
]

const invoicedOptions: Array<{ label: string; value: '' | 'si' | 'no' }> = [
  { label: 'Facturadas y sin facturar', value: '' },
  { label: 'Solo facturadas', value: 'si' },
  { label: 'Solo sin facturar', value: 'no' },
]

/**
 * Filtro de anticipos.
 *
 * «Con anticipo» y «Sin anticipo», como el de facturación. Se escribe así y no como
 * una casilla de marcado porque todos los filtros de esta pantalla son desplegables:
 * uno de otro tipo rompe la fila y obliga a mirar dos veces.
 */
const advanceOptions: Array<{ label: string; value: '' | 'si' | 'no' }> = [
  { label: 'Con y sin anticipos', value: '' },
  { label: 'Solo con anticipo', value: 'si' },
  { label: 'Solo sin anticipo', value: 'no' },
]

const attentionLineOptions: Array<{ label: string; value: string | '' }> = [
  { label: 'Todas las líneas', value: '' },
  { label: 'Solo con línea', value: 'con_linea' },
  { label: 'Sin línea', value: 'sin_linea' },
]

const advisorOptions = computed<Array<{ label: string; value: number | '' }>>(() => [
  { label: 'Todos los asesores', value: '' },
  ...advisors.value.map((advisor) => ({ label: advisor.name, value: advisor.id })),
])

/** Los valores de cada desplegable, para validar lo que llega por la URL. */
function valoresDe(opciones: Array<{ value: string | number }>): (string | number)[] {
  return opciones.map((opcion) => opcion.value)
}

/*
| Los filtros viven en la URL
|--------------------------------------------------------------------------
|
| Filtrabas, abrías una cotización, volvías, y el filtro había desaparecido. No era el
| botón de atrás: es que los filtros estaban en `ref`s del componente, y un componente no
| sobrevive a que te vayas a otra vista. Al volver se crea otro, con los `ref` vacíos. El
| filtro no tenía ningún sitio donde quedarse.
|
| Aquí sí lo tiene. Y `conQuery` es la otra mitad: «Volver» empuja una entrada **nueva** al
| listado, así que el historial por sí solo no basta —la entrada anterior deja de usarse— y
| los enlaces a la ficha se llevan los filtros encima.
|
| Las listas de `permitidos` salen de las opciones del propio filtro, para que no haya dos
| sitios donde se acepte el mismo estado. Sin ellas, `?estado=inventado` se aplicaría
| como filtro y la consulta saldría con un estado que el backend no entiende.
|
| El asesor va con `entero` y no con `permitidos` a propósito: su lista llega de la API
| después de montar, así que al leer la URL todavía no se conoce. Un id que ya no exista da
| una lista vacía —que es lo que pidió el enlace— y no la lista entera.
*/
const filtros = useFiltrosEnUrl({
  busqueda: { ref: search, texto: true },
  estado: { ref: status, permitidos: valoresDe(statusOptions) },
  tipo: { ref: priceType, permitidos: valoresDe(priceTypeOptions) },
  seguimiento: { ref: followUp, permitidos: valoresDe(followUpOptions) },
  asesor: { ref: advisorId, entero: true },
  facturacion: { ref: invoiced, permitidos: valoresDe(invoicedOptions) },
  linea_atencion: { ref: attentionLineFilter, permitidos: valoresDe(attentionLineOptions) },
  anticipo: { ref: advanceFilter, permitidos: valoresDe(advanceOptions) },
  desde: { ref: dateFrom, fecha: true },
  hasta: { ref: dateTo, fecha: true },
  pagina: { ref: page, numero: true },
})

/** Cuántos filtros hay activos (excluyendo paginación). */
const filtrosContador = computed(() =>
  Object.keys(filtros.consulta).filter((k) => k !== 'pagina').length
)

/**
 * La celda del estado: un desplegable, no una etiqueta.
 *
 * Antes había que pulsar un icono, esperar a un diálogo, elegir el estado y confirmar.
 * Cuatro pasos para lo que en el listado es cambiar una casilla, y cambiar el estado es
 * la acción más frecuente del día de un comercial: una cotización se manda, se manda
 * otra vez, se cancela.
 *
 * El desplegable va **en la fila** y no en un diálogo aparte, así que el estado sigue
 * teniendo un único sitio donde se decide: no hay una lista de estados en dos sitios que
 * se puedan ir de la sincronía.
 *
 * La única transición que pide confirmación es `cancelada`, porque saca la cotización del
 * trabajo en curso y no debe ser un accidentazo de un dedo.
 */
/**
 * El estado como texto con un punto de color delante.
 *
 * El punto es el que hace de etiqueta: seis píxeles que se leen de un vistazo en toda la
 * columna. Con el texto encima —no dentro de una cápsula— el estado completo se lee, que era
 * lo que faltaba.
 */
function estadoConPunto(texto: string, severidad: EstadoSeverity): VNode {
  return h('span', { class: 'flex items-center gap-1.5' }, [puntoDeEstado(severidad), h('span', texto)])
}

/** El punto de color del estado. */
function puntoDeEstado(severidad: EstadoSeverity): VNode {
  return h('span', {
    'data-estado': severidad,
    'aria-hidden': 'true',
    class: 'inline-block size-1.5 shrink-0 rounded-full',
    style: { background: colorDelEstado(severidad) },
  })
}

/**
 * El color del punto.
 *
 * Sale del token del propio estado y no de una lista de colores aquí: una segunda lista de
 * colores por severidad es una que se queda vieja cuando el tema cambia uno, y entonces los
 * puntos salen del color antiguo sin que nada avise.
 */
function colorDelEstado(severidad: EstadoSeverity): string {
  // `warn` es como PrimeVue llama a `warning` en su tipo de severidad. Va en el mapa y no se
  // traduce al revés: si se tradujera, un estado que llegue como `warning` saldría sin color
  // sin ningún aviso.
  const token = {
    warn: 'var(--color-amber-500)',
    success: 'var(--color-green-500)',
    warning: 'var(--color-amber-500)',
    danger: 'var(--color-red-500)',
    info: 'var(--color-blue-500)',
    secondary: 'var(--color-surface-400)',
  }[severidad]

  return token ?? 'var(--color-surface-400)'
}

/** Los valores que PrimeVue acepta como severidad de una etiqueta. */
type EstadoSeverity = 'success' | 'info' | 'secondary' | 'danger' | 'warning' | 'warn'

function statusCell(row: unknown): unknown {
  const quote = row as Quote

  if (!canManage.value) {
    return h(Tag, { value: statusLabel(quote.status), severity: statusSeverity(quote.status) })
  }

  return h(
    Select,
    {
      modelValue: quote.status,
      options: estadosDeFila,
      optionLabel: 'label',
      optionValue: 'value',
      'aria-label': `Estado de la cotización de ${quote.customer_name}`,
      class: 'select-compacto w-full',
      onChange: (evento: { value: QuoteStatus }) => pedirCambioDeEstado(quote, evento.value),
    },
    /*
    | El color del estado, con un punto y no con una etiqueta
    |
    | Aquí había dos recuadros dentro de recuadros: el del desplegable y el de la etiqueta.
    | Y salía peor: la etiqueta mide unos 72 px con su relleno de `px-2.5`, y la caja del
    | desplegable dejaba 62. El estado se leía **«PENDIENT»**, partido por la mitad, que no
    | es un dato: es un texto que no se puede leer.
    |
    | El color sigue estando, que es lo que se quería —con veinte filas, buscar el estado es lo
    | primero que se hace, y se hacía leyendo colores—, pero con un punto: seis píxeles en vez
    | de setenta y dos, y sin dibujar otro recuadro dentro del control.
    */
    {
      value: (ctx: { value: QuoteStatus }) => estadoConPunto(statusLabel(ctx.value), statusSeverity(ctx.value)),
      option: (ctx: { option: { label: string; value: QuoteStatus } }) =>
        h('span', { class: 'flex items-center gap-2' }, [
          puntoDeEstado(statusSeverity(ctx.option.value)),
          h('span', ctx.option.label),
        ]),
    },
  )
}

/**
 * Aplica un cambio de estado desde la fila.
 *
 * Va al mismo `applyStatus()` que el diálogo, así que no hay dos caminos que se puedan ir
 * de la sincronía: uno deja la fila actualizada y el otro no.
 */
async function pedirCambioDeEstado(quote: Quote, nuevo: QuoteStatus): Promise<void> {
  if (nuevo === quote.status) return

  if (nuevo === 'cancelada') {
    // Pide confirmación con el diálogo, que ya sabe decir de quién es la cotización.
    openStatusDialog(quote, nuevo)

    return
  }

  statusTarget.value = quote
  newStatus.value = nuevo
  statusError.value = null

  await applyStatus()
}

/**
 * La celda del cliente: nombre, correo y las notas sin leer debajo.
 *
 * El correo debajo del nombre es lo que deshace las homonimías —dos «Jhonatan
 * Restrepo»— y de paso dice cómo se le alcanza. La aplicación de referencia ya lo traía
 * y quien viene de ella lo busca.
 *
 * El contador de notas era una columna entera para un número. Junto al cliente sigue a
 * la vista —que es de lo que se trata, es lo que hace abrir la cotización— y libera una
 * columna para el seguimiento.
 */
function clienteCell(row: unknown): unknown {
  const quote = row as Quote

  const hijos: VNodeChild[] = [
    h(
      'span',
      { class: 'block font-medium break-words text-surface-900 dark:text-surface-100' },
      quote.customer_name,
    ),
  ]

  if (quote.customer_email !== null && quote.customer_email !== '') {
    hijos.push(
      h(
        'span',
        { class: 'block text-xs break-words text-surface-500 dark:text-surface-400' },
        quote.customer_email,
      ),
    )
  }

  // Cuántos productos contiene la cotización, contando los de los sets. Es
  // el tamaño real del pedido: un set con tres artículos son tres productos,
  // no uno. Va aquí y no en columna propia porque la tabla ya no cabe: los
  // anchos declarados suman más de lo que hay en la pantalla.
  hijos.push(
    h(
      'span',
      { class: 'block text-sm font-medium text-surface-500 dark:text-surface-400' },
      `${quote.products_count} ${quote.products_count === 1 ? 'producto' : 'productos'}`,
    ),
  )

  if (quote.unread_notes_count > 0) {
    hijos.push(
      h(
        'span',
        {
          class: 'mt-0.5 inline-flex w-fit items-center gap-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900 dark:text-amber-200',
          title: `${quote.unread_notes_count} nota(s) nueva(s) de seguimiento`,
        },
        [h('i', { class: 'pi pi-comment text-[0.65rem]' }), String(quote.unread_notes_count)],
      ),
    )
  }

  // El ancho viene en la columna. Sin `min-w-0` el contenedor flexible no cede: el
  // `truncate` de los hijos necesita que su padre pueda encogerse por debajo del ancho
  // del texto, y sin esto el nombre largo empuja la columna y el `width: 190px` no
  // sirve de nada.
  return h('span', { class: 'flex min-w-0 flex-col gap-0.5' }, hijos)
}

/**
 * La celda del seguimiento: la fecha del próximo contacto.
 *
 * Es la columna que más se echa de menos si no está. El comercial abre el listado para
 * saber a quién llamar hoy, y con la fecha de la cotización y el total no puede
 * contestar eso. Hay un filtro de seguimiento, pero un filtro esconde lo que se busca en
 * lugar de enseñarlo.
 *
 * Las **vencidas** salen marcadas. Un filtro de «vencidas» ayuda a encontrarlas; verlas
 * marcadas al pasar por la lista es lo que dice a quién tocar el timbre hoy. El listado
 * de referencia enseñaba tres guiones.
 *
 * La comparación es entre fechas ISO y no con `Date`: `"2026-10-02"` sin zona es
 * medianoche UTC, y `new Date` en zona local desplaza un día. El día que decide aquí es
 * justo el que separa «vencida» de «hoy».
 */
/**
 * Los dos avisos de la fila, en una sola columna.
 *
 * Lo que se mira al recorrer el listado no es la fecha del seguimiento ni el importe
 * adelantado: es si hay algo pendiente de hacer con esa cotización. Eso cabe en dos
 * iconos con su color, y las dos columnas que ocupaban para decirlo —198px— se
 * ahorran en 76.
 *
 * El color es el que dice el estado, porque es lo que se ve de reojo al recorrer:
 *
 * - **Seguimiento vencido**, en rojo, con el icono de aviso que ya se usaba.
 * - **Seguimiento para hoy**, en ámbar: todavía no está vencido, pero es el último día
 *   para:callarlo.
 * - **Seguimiento más adelante**, en gris: hay uno, y no es lo urgente.
 * - **Anticipo**, en verde, con el icono de cartera. Solo cuando lo hay: una columna de
 *   nadas es ruido.
 *
 * Cada icono lleva su fecha o su importe en el `title` y en el nombre accesible, así que
 * el dato fino no se pierde: se lee pasando por encima o con un lector de pantalla. Lo
 * que no cabe ya es la fecha en la propia celda, y esa sigue entera en la tarjeta de
 * móvil, en la ficha y en la pantalla de seguimientos.
 *
 * Los dos iconos van en **huecos fijos** y no pegados uno al otro. Si cada uno se
 * apareciera solo cuando tiene algo, las filas no quedarían alineadas y el ojo no
 * encontraría la columna: dos columnas de iconos que bailan son dos columnas que hay que leer una a
 * una, que es justo lo que se quería evitar al juntarlas.
 */
function avisosCell(row: unknown): unknown {
  const quote = row as Quote
  const fecha = quote.next_follow_up_date

  const avisos: VNode[] = [
    fecha !== null && fecha !== ''
      ? h(
          'span',
          {
            role: 'img',
            'aria-label': etiquetaSeguimiento(fecha),
            title: etiquetaSeguimiento(fecha),
            class: 'flex h-4 w-4 shrink-0 items-center justify-center',
          },
          [h('i', { class: iconoSeguimiento(fecha), 'aria-hidden': 'true' })],
        )
      : h('span', { class: 'h-4 w-4 shrink-0' }),

    quote.has_advances
      ? h(
          'span',
          {
            role: 'img',
            'aria-label': `Anticipos: ${formatCurrency(quote.advances_total)} de ${formatCurrency(quote.total_price)}`,
            title: `${formatCurrency(quote.advances_total)} anticipados de ${formatCurrency(quote.total_price)}`,
            class: 'flex h-4 w-4 shrink-0 items-center justify-center text-emerald-600 dark:text-emerald-400',
          },
          [h('i', { class: 'pi pi-wallet', 'aria-hidden': 'true' })],
        )
      : h('span', { class: 'h-4 w-4 shrink-0' }),
  ]

  return h('span', { class: 'flex items-center gap-2' }, avisos)
}

/**
 * El icono del seguimiento y el color que le toca.
 *
 * Tres estados y tres colores, con un icono distinto para el vencido porque es el único
 * que es una urgencia de verdad:
 *
 * - **Vencido**, rojo y con el icono de aviso: hay que llamar y ya se pasó el día.
 * - **Para hoy**, ámbar: no está vencido, pero es el último día.
 * - **Más adelante**, gris: hay uno, y no corre prisa.
 */
function iconoSeguimiento(fecha: string): string {
  if (seguimientoVencido(fecha)) return 'pi pi-exclamation-circle text-sm text-red-500'

  if (fecha === hoyIso.value) return 'pi pi-bell text-sm text-amber-500'

  return 'pi pi-bell text-sm text-surface-400 dark:text-surface-500'
}

/**
 * Lo que dice el icono del seguimiento, en una frase.
 *
 * «Vencido», «para hoy» o la fecha. El color va con la palabra porque con el color
 * solo no se distingue una señal de otra cuando el color no se ve: una foto en escala
 * de grises, un daltonismo, una pantalla sucia. El color es un aviso; la palabra es el
 * dato.
 */
function etiquetaSeguimiento(fecha: string): string {
  if (seguimientoVencido(fecha)) return `Seguimiento vencido el ${formatShortDate(fecha)}`

  if (fecha === hoyIso.value) return 'Seguimiento para hoy'

  return `Seguimiento el ${formatShortDate(fecha)}`
}

/** La celda del tipo de facturación. */
function tipoCell(row: unknown): unknown {
  const quote = row as Quote

  return h(
    'span',
    {
      class: 'whitespace-nowrap rounded px-1 py-0.5 text-xs font-medium',
      'data-tipo': quote.price_type,
    },
    quote.price_type === 'wholesale' ? 'Mayorista' : 'Detal',
  )
}

/**
 * La celda de anticipos.
 *
 * Solo dice algo cuando hay anticipos. Poner «—» en las que no lo tienen llenaría
 * la columna de nadas, y una columna de nadas no es información: lo que importa es
 * qué cotizaciones tienen dinero adelantado, y eso se ve con la insignia.
 */
function advanceCell(row: unknown): unknown {
  const quote = row as Quote

  if (!quote.has_advances) {
    return h('span', { class: 'text-surface-400' }, '—')
  }

  return h(
    'span',
    {
      class: 'inline-flex items-center gap-1 rounded bg-emerald-100 px-1.5 py-0.5 text-xs font-medium text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200',
      title: `${formatCurrency(quote.advances_total)} anticipados de ${formatCurrency(quote.total_price)}`,
    },
    [h('i', { class: 'pi pi-wallet', 'aria-hidden': 'true' }), formatCurrency(quote.advances_total)],
  )
}

/**
 * La celda de facturación.
 *
 * Columna aparte del estado porque son dos cosas distintas: una cotización puede estar
 * `enviada` y no facturada, o `separada` y ya facturada. Con una sola columna había que
 * mirar el número de factura para saber si estaba facturada, y lo que se mira de un
 * vistazo es cuántas hay facturadas.
 *
 * El número va debajo y no en lugar del estado porque el estado es lo que se cuenta y
 * el número es lo que se busca cuando ya se sabe cuál. Una cotización sin facturar dice
 * `PENDIENTE`, no un guion: un guion no dice si falta facturar o si es que no se miró.
 */
function invoicedCell(row: unknown): unknown {
  const quote = row as Quote
  const facturada = esFacturada(quote)

  return h('span', { class: 'flex flex-col items-start gap-0.5' }, [
    h(Tag, {
      value: facturada ? 'FACTURADA' : 'PENDIENTE',
      severity: facturada ? 'success' : 'secondary',
    }),
    facturada && quote.invoice_number
      ? h('span', { class: 'font-mono text-xs text-surface-500 dark:text-surface-400' }, quote.invoice_number)
      : null,
  ])
}

/**
 * Las columnas, en el orden en que se leen.
 *
 * **Fecha la primera.** Un listado de cotizaciones se mira por fecha: es lo que ordena
 * el trabajo del día. Con el cliente delante había que saltar por encima para llegar a
 * ella, y era la única columna que ya venía formateada en el resource.
 *
 * Las que se esconden en móvil son las de apoyo —seguimiento, tipo, asesor,
 * facturación— porque en el ancho de un teléfono la tabla tiene que dejar tres
 * columnas con aire para que los importes se lean. El contenido que no cabe no se pierde:
 * la tarjeta de móvil lleva el total, la fecha y las notas nuevas.
 */
/*
| Los anchos de las columnas
|--------------------------------------------------------------------------
|
| Cada columna lleva su ancho, y no solo la de Cliente. Sin anchos, el sobrante se lo reparte
| el navegador como le parece, y la de Cliente —la única con contenido largo— se quedaba con
| casi todo mientras las demás se quedaban sin sitio.
|
| Los anchos salen de lo que lleva cada celda, no de un número redondo:
|
|     Fecha         94px  una fecha
|     Cliente      162px  nombre y correo; el texto salta de línea
|     Avisos        68px  dos iconos de 16 en huecos fijos, más su hueco
|     Tipo          92px  la etiqueta «Mayorista», que es la más larga
|     Línea at.     88px  una línea de atención corta
|     Asesor        94px  un nombre; el texto salta de línea
|     Total         90px  un importe con su formato; el texto salta de línea
|     Logística    136px  el desplegable con el estado **entero** y su flecha
|     Facturación  104px  etiqueta y número de factura
|     Acciones     128px  cuatro botones cuadrados de 1.75rem
|
| La suma da **1166px**, y el hueco de verdad son **1174px**: medidos en el navegador, el
| tope del contenedor son 1280, el relleno del sitio 64 y el de la tarjeta 42. Quedan 8 de
| sobra, y el test `la tabla cabe en la pantalla` mide las dos cifras **y todas las
| columnas**, para que subir un ancho o añadir una se note en el test y no en el
| navegador.
|
| Antes la cuenta daba 976 contra 1025 y parecía sobrada, pero la suma se hacía con nueve
| columnas de las doce que había: las tres que faltaban sumaban 286px. Con eso, la tabla
| se salía 88px por debajo de la columna fija de Facturación y lo que tapaba era el borde
| del desplegable de Logística. Ahora, con las dos columnas de avisos en una sola, la
| tabla entra entera y el estado se lee sin puntos suspensivos.
|
| Los anchos solo valen con `layout="fixed"`. En el reparto automático, una columna crece si
| su contenido no cabe en lo declarado: Cliente pedía 164 y el nombre la empujaba, y la
| tabla se salía igual. Con reparto fijo el ancho declarado **es** el ancho.
|
| Y con reparto fijo hay que decir **qué se hace con el texto que no cabe**. En Cliente,
| Asesor y Total salta de línea —una fila más alta y el nombre entero—; en el resto se
| recorta con puntos, que está bien para un dato corto que se reconoce de sobra.
|
| El Total también salta, y es el caso menos intuitivo: un importe recortado no parece un
| importe pequeño, parece un importe que no cabe. Un total con dos renglones se lee; un total
| partido se confunde con otro importe.
*/

const visibleColumns = [
  { field: 'quote_number_label', header: 'Nº Cot.', style: 'width: 110px' },
  { field: 'quote_date_label', header: 'Fecha', style: 'width: 94px' },
  { field: 'customer_name', header: 'Cliente', template: clienteCell, style: 'width: 162px' },
  // Seguimiento y anticipo eran dos columnas y ahora son una. No por falta de sitio en
  // el papel —sumaban 198px— sino porque la tabla **no cabía**: medidos en el navegador,
  // los anchos declarados dan 1262px y el hueco real son 1174, así que el sobrante se
  // iba por debajo de la columna fija de Facturación y lo último que se veía era el
  // borde derecho del desplegable de Logística. Ese dato no estaba escondido por
  // decisión: estaba tapado.
  { field: 'avisos_label', header: 'Avisos', template: avisosCell, hideOnMobile: true, style: 'width: 68px' },
  { field: 'price_type_label', header: 'Tipo', template: tipoCell, hideOnMobile: true, style: 'width: 92px' },
  { field: 'attention_line_label', header: 'Línea at.', hideOnMobile: true, style: 'width: 88px' },
  // Asesor y total saltan de línea en vez de recortarse: con puntos suspensivos no se sabe
  // si el nombre es largo o si no cabe, y un importe recortado parece un importe pequeño.
  { field: 'advisor_label', header: 'Asesor', hideOnMobile: true, style: 'width: 94px', wrap: true },
  { field: 'total_label', header: 'Total', style: 'width: 90px', wrap: true },
  { field: 'status_label', header: 'Logística', template: statusCell, style: 'width: 136px' },
  // La de facturación **no** se esconde en móvil. Es corta —una etiqueta— y es la
  // pregunta que se hace al recorrer la lista: cuánto de esto ya está cobrado. Escapaba
  // por ser la última, y no por ocupar sitio.
  { field: 'invoiced_label', header: 'Facturación', template: invoicedCell, style: 'width: 104px' },
]

/*
| Las dos columnas que no se van de la vista
|--------------------------------------------------------------------------
|
| Esta tabla tiene nueve columnas y no caben en la pantalla de un portátil: lo que se
| sale por la derecha es siempre lo último, y lo último era precisamente lo que más se
| usa. Con la tabla «desbordada» —como estaba— no había ni una pista de que faltara
 * nada: se veía cortada en el borde y sin scrolls, y era fácil pensar que la lista no
 * tenía facturación ni acciones.
 *
 * Se fijan al borde derecho y las demás pasan por debajo al desplazar. El ancho no es
 * decorativo: lo necesita `DenseTable` para saber dónde empieza la siguiente.
 */
const columnasFijadas = [{ field: 'invoiced_label', width: 104 }]

const rows = computed(() =>
  items.value.map((quote) => ({
    ...quote,
    quote_number_label: quote.quote_number ?? '—',
    status_label: statusLabel(quote.status),
    quote_date_label: formatShortDate(quote.quote_date),
    total_label: formatCurrency(quote.total_price),
    // El valor se calcula en `invoicedCell`; aquí solo queda el nombre del campo
    // que la columna busca. `is_invoiced` puede venir falso con número puesto, y en
    // ese caso la cotización **sí** está facturada: manda el número.
    invoiced_label: quote.is_invoiced ? 'FACTURADA' : 'PENDIENTE',
    attention_line_label: quote.attention_line ?? '—',
    anticipo_label: quote.has_advances ? quote.advances_total : '—',
    advisor_label: quote.user_name ?? '—',
    /*
     * Cuatro acciones y solo iconos: ver, editar, facturar y borrar.
     *
     * Ver y editar en la fila, no solo en la ficha: recorrer una lista para corregir
     * cinco cotizaciones abriendo cada una es un paso de más en cada una.
     *
     * Facturar y borrar salen **solo** si la cotización está en un estado en el que
     * tienen sentido, porque un botón que no hace nada es peor que un botón que no
     * está. Una ya facturada no se vuelve a facturar, y una borrada no se borra dos
     * veces.
     *
     * El cambio de estado **no** está aquí: vive en el desplegable de la columna
     * Logística, que es donde lo busca quien lo va a cambiar. Un icono aparte sería un
     * segundo camino a lo mismo.
     */
    actions: [
      { label: 'Ver detalle', value: 'view', icon: 'pi pi-eye' },
      ...(canManage.value
        ? [
            { label: 'Editar', value: 'edit', icon: 'pi pi-pencil' },
            ...(quote.is_invoiced
              ? []
              : [
                  {
                    label: 'Marcar como facturada',
                    value: 'invoice',
                    icon: 'pi pi-receipt',
                  },
                ]),
            { label: 'Borrar', value: 'delete', icon: 'pi pi-trash', severity: 'danger' },
          ]
        : []),
    ],
  })),
)

const hasFilters = computed(
  () =>
    search.value.trim() !== '' ||
    status.value !== '' ||
    priceType.value !== '' ||
    followUp.value !== '' ||
    advisorId.value !== '' ||
    invoiced.value !== '',
)

const isEmpty = computed(() => !loading.value && errorMessage.value === null && items.value.length === 0)

/**
 * Hoy, en ISO, para comparar con `next_follow_up_date`.
 *
 * Como cadena y no como `Date`: las fechas del servidor vienen como `"2026-10-02"` y
 * `new Date("2026-10-02")` es medianoche **UTC**. Compararlas en zona local desplaza un
 * día, y el día que importa aquí es justo el que decide si hay que llamar a alguien hoy.
 * Dos fechas ISO se comparan bien con `<`, sin pasar por `Date`.
 *
 * Se recalcula en cada `load()`, no una sola vez al abrir la pantalla: una pestaña
 * abierta de noche seguiría marcando como vencida una fecha que ya pasó a ser hoy.
 */
const hoyIso = ref('')

function seguimientoVencido(fecha: string | null): boolean {
  return fecha !== null && fecha !== '' && hoyIso.value !== '' && fecha < hoyIso.value
}

/**
 * Si una cotización está facturada.
 *
 * El número manda sobre el interruptor: puede venir `false` con el número puesto, y en
 * ese caso **sí** está facturada. La tabla y la tarjeta lo preguntaban cada una por su
 * lado, y dos copias de una condición es una que se queda vieja en una de las dos.
 */
function esFacturada(quote: Quote): boolean {
  return quote.is_invoiced || (quote.invoice_number ?? '') !== ''
}

const rangeLabel = computed(() => {
  if (total.value === 0) return ''
  const first = (page.value - 1) * perPage + 1
  const last = Math.min(page.value * perPage, total.value)
  return `${first}–${last} de ${total.value}`
})

function extractErrorMessage(cause: unknown): string {
  const maybeAxios = cause as { response?: { data?: { error?: { message?: string } } } }
  return maybeAxios.response?.data?.error?.message ?? 'No se pudieron cargar las cotizaciones. Revisa tu conexión e inténtalo de nuevo.'
}

/** La fecha de hoy en ISO, para saber qué seguimientos están vencidos. */
function hoy(): string {
  const ahora = new Date()

  return [
    ahora.getFullYear(),
    String(ahora.getMonth() + 1).padStart(2, '0'),
    String(ahora.getDate()).padStart(2, '0'),
  ].join('-')
}

async function load(): Promise<void> {
  // Se recalcula en cada carga: una pestaña abierta de noche seguiría marcando como
  // vencida una fecha que ya pasó a ser hoy.
  hoyIso.value = hoy()

  loading.value = true
  errorMessage.value = null

  try {
    const { data } = await quoteApi.list({
      search: search.value.trim() || undefined,
      status: status.value || undefined,
      price_type: priceType.value || undefined,
      follow_up_status: followUp.value || undefined,
      user_id: advisorId.value === '' ? undefined : advisorId.value,
      // Sin filtro no se manda la clave. La conversión a `1`/`0` la hace el
      // servicio, que es donde se sabe qué acepta el backend.
      is_invoiced: invoiced.value === '' ? undefined : invoiced.value === 'si',
      has_advances: advanceFilter.value === '' ? undefined : advanceFilter.value === 'si',
      date_from: dateFrom.value || undefined,
      date_to: dateTo.value || undefined,
      page: page.value,
      per_page: perPage,
    })

    items.value = data.data
    total.value = data.meta.total
  } catch (cause: unknown) {
    items.value = []
    total.value = 0
    errorMessage.value = extractErrorMessage(cause)
  } finally {
    loading.value = false
  }
}

let debounceHandle: ReturnType<typeof setTimeout> | null = null

watch(search, () => {
  page.value = 1
  if (debounceHandle !== null) clearTimeout(debounceHandle)
  // Sin debounce, cada tecla dispara una petición y las respuestas pueden
  // llegar desordenadas, pintando resultados de un término anterior.
  debounceHandle = setTimeout(() => {
    void load()
  }, 300)
})

watch([status, priceType, followUp, advisorId, invoiced, advanceFilter], () => {
  page.value = 1
  void load()
})

watch(page, () => {
  void load()
})

onMounted(async () => {
  // El filtro de asesor es la única parte que necesita un segundo pedido. Si
  // falla, el listado sigue siendo utilizable sin ese filtro, así que su error
  // no se mezcla con el del listado.
  try {
    const { data } = await quoteApi.advisors()
    // El `catch` solo cubre los fallos de red. Si la respuesta llega bien formada
    // pero sin lista, la asignación deja `advisors` sin array y el computed de las
    // opciones del filtro revienta al pintarse: el listado entero se queda en
    // «todavía no hay cotizaciones», sin decir que ha fallado. Con la lista vacía el
    // filtro aparece sin opciones y las cotizaciones se ven igual.
    advisors.value = Array.isArray(data.data) ? data.data : []
  } catch {
    advisors.value = []
  }

  await load()
})

/**
 * Cambio de estado desde el listado.
 *
 * Va aquí y no en un desplegable dentro de la columna porque el estado es una de las
 * cinco columnas del listado y un desplegable ahí no cabe; y porque un `<select>` en
 * cada fila convierte el listado en un editor, de modo que un clic accidental al
 * recorrerlo con el ratón cambia el estado de una cotización real.
 *
 * El diálogo **no** está también en la ficha. Si el cambio se hace desde el listado,
 * dejarlo en los dos sitios es la forma habitual de que se comporten distinto.
 */
/*
| Facturar y borrar desde la fila.
|
| Los dos necesitan algo antes de ocurrir —el número de factura, o una confirmación— y
| por eso son diálogos y no botones que disparan y ya. Facturar sin número deja una
| factura que no se puede auditar, y borrar una cotización con sus artículos y sus notas
| no tiene vuelta atrás.
*/

const invoiceDialogVisible = ref(false)
const invoiceTarget = ref<Quote | null>(null)
const invoiceNumber = ref('')
const deleteDialogVisible = ref(false)
const deleteTarget = ref<Quote | null>(null)
const deleteDialog = ref<{ close: () => void } | null>(null)
const rowActing = ref(false)
const rowError = ref<string | null>(null)

function openInvoiceDialog(row: unknown): void {
  const quote = row as Quote

  invoiceTarget.value = quote
  invoiceNumber.value = quote.invoice_number ?? ''
  rowError.value = null
  invoiceDialogVisible.value = true
}

async function markInvoiced(): Promise<void> {
  if (invoiceTarget.value === null) return

  // Sin número no se guarda. El backend lo rechaza con un 422 y el trabajo se pierde;
  // comprobarlo aquí es un mensaje en el sitio donde se ha escrito el número, que es
  // donde hay que corregirlo.
  if (invoiceNumber.value.trim() === '') {
    rowError.value = 'Escribe el número de la factura.'

    return
  }

  rowActing.value = true
  rowError.value = null

  try {
    const { data } = await quoteApi.updateStatus(invoiceTarget.value.id, {
      status: invoiceTarget.value.status === 'borrador' ? 'pendiente' : invoiceTarget.value.status,
      is_invoiced: true,
      invoice_number: invoiceNumber.value.trim(),
    })

    // Se sustituye la fila en vez de recargar la página entera: el resto de la lista se
    // queda como estaba y no se pierde la posición ni el scroll.
    items.value = items.value.map((item) => (item.id === data.data.id ? data.data : item))
    invoiceDialogVisible.value = false
  } catch (cause: unknown) {
    const maybeAxios = cause as { response?: { data?: { error?: { message?: string } } } }

    rowError.value =
      maybeAxios.response?.data?.error?.message ?? 'No se pudo marcar como facturada.'
  } finally {
    rowActing.value = false
  }
}

function openDeleteDialog(row: unknown): void {
  deleteTarget.value = row as Quote
  rowError.value = null
  deleteDialogVisible.value = true
}

async function removeQuote(): Promise<void> {
  if (deleteTarget.value === null) {
    console.log('[removeQuote] deleteTarget es null, saliendo')
    return
  }

  console.log('[removeQuote] Iniciando borrado de:', deleteTarget.value.id, deleteTarget.value.quote_number)
  rowActing.value = true
  rowError.value = null

  try {
    console.log('[removeQuote] Llamando a quoteApi.remove...')
    await quoteApi.remove(deleteTarget.value.id)
    console.log('[removeQuote] API responded, filtrando items...')
    items.value = items.value.filter((item) => item.id !== deleteTarget.value?.id)
    console.log('[removeQuote] Items filtrados, cerrando dialogo...')
    // No llamar a close() explícitamente; el v-model se encarga de cerrar al emitir update:visible
    deleteDialogVisible.value = false
    console.log('[removeQuote] deleteDialogVisible = false')

    if (items.value.length === 0 && page.value > 1) {
      page.value -= 1
      await load()

      return
    }

    total.value = Math.max(0, total.value - 1)
  } catch (cause: unknown) {
    console.log('[removeQuote] ERROR:', cause)
    const maybeAxios = cause as { response?: { data?: { error?: { message?: string } } } }

    rowError.value = maybeAxios.response?.data?.error?.message ?? 'No se pudo borrar la cotización.'
  } finally {
    console.log('[removeQuote] Finally, rowActing = false')
    rowActing.value = false
  }
}

const statusDialogVisible = ref(false)
const statusTarget = ref<Quote | null>(null)
const newStatus = ref<QuoteStatus>('pendiente')
const statusChanging = ref(false)
const statusError = ref<string | null>(null)

/**
 * Abre el diálogo de cambio de estado.
 *
 * `destino` es para cuando quien llama ya sabe a qué estado va: la fila propone
 * `cancelada` y el diálogo solo confirma. Sin él, el diálogo empieza en el estado
 * actual, que es lo que se necesita cuando se abre para elegir.
 */
function openStatusDialog(row: unknown, destino?: QuoteStatus): void {
  const quote = row as Quote

  statusTarget.value = quote
  newStatus.value = destino ?? quote.status
  statusError.value = null
  statusDialogVisible.value = true
}

async function applyStatus(): Promise<void> {
  if (statusTarget.value === null) return

  statusChanging.value = true
  statusError.value = null

  try {
    const { data } = await quoteApi.updateStatus(statusTarget.value.id, { status: newStatus.value })

    // Se reemplaza la fila en la lista en vez de recargarla entera: recargar el listado
    // pierde la página en la que se estaba y el scroll, y el usuario iba a cambiar más
    // de un estado seguidos.
    items.value = items.value.map((item) => (item.id === data.data.id ? data.data : item))

    statusDialogVisible.value = false
  } catch (cause: unknown) {
    const maybeAxios = cause as { response?: { data?: { error?: { message?: string } } } }

    statusError.value = maybeAxios.response?.data?.error?.message ?? 'No se pudo cambiar el estado.'
  } finally {
    statusChanging.value = false
  }
}

function handleAction(row: unknown, action: string): void {
  if (action === 'view') {
    abrirFicha(row as Quote)
    return
  }

  if (action === 'edit') {
    abrirEditor(row as Quote)
    return
  }

  if (action === 'invoice') {
    openInvoiceDialog(row)
  }

  if (action === 'delete') {
    openDeleteDialog(row)
  }

  if (action === 'status') {
    openStatusDialog(row)
  }
}

function onPageChange(event: { page: number }): void {
  page.value = event.page + 1
}

/**
 * Los filtros que hay puestos, en texto.
 *
 * Se leen de las **opciones** del filtro, no de los valores: el select muestra
 * «Solo mayorista» y no `wholesale`. Poner el valor crudo sería una lista de paraxes
 * que nadie entiende, y para eso ya está la URL.
 */
function etiquetasDeFiltro(): Array<{ texto: string }> {
  const etiquetas: Array<{ texto: string }> = []

  if (search.value.trim() !== '') etiquetas.push({ texto: `«${search.value.trim()}»` })

  // El rango se lee como una sola etiqueta y no como dos fechas sueltas: en el
  // resumen caben varias etiquetas en línea, y dos `01/10/2026` pegados sin decir
  // cuál es el principio y cuál el final no se distinguen de un vistazo.
  if (dateFrom.value !== '' && dateTo.value !== '') {
    etiquetas.push({ texto: `${formatShortDate(dateFrom.value)} – ${formatShortDate(dateTo.value)}` })
  } else if (dateFrom.value !== '') {
    etiquetas.push({ texto: `Desde ${formatShortDate(dateFrom.value)}` })
  } else if (dateTo.value !== '') {
    etiquetas.push({ texto: `Hasta ${formatShortDate(dateTo.value)}` })
  }

  const pares: Array<[unknown, Array<{ label: string; value: string | number }>]> = [
    [status.value, statusOptions],
    [priceType.value, priceTypeOptions],
    [followUp.value, followUpOptions],
    [advisorId.value, advisorOptions.value],
    [invoiced.value, invoicedOptions],
    [advanceFilter.value, advanceOptions],
    [attentionLineFilter.value, attentionLineOptions],
  ]

  for (const [valor, opciones] of pares) {
    if (valor === '' || valor === null || valor === undefined) continue

    const encontrada = opciones.find((opcion) => opcion.value === valor)
    etiquetas.push({ texto: encontrada?.label ?? String(valor) })
  }

  return etiquetas
}

/**
 * Abrir la ficha y el editor **con los filtros encima**.
 *
 * Sin esto, «Volver» desde la ficha empuja una entrada nueva al listado sin la consulta, y
 * el listado vuelve a mostrarlos todos. El historial no salva: la entrada anterior, la que
 * tenía los filtros, deja de ser la que se está usando.
 */
function abrirFicha(quote: Quote): void {
  void router.push(filtros.conQuery({ name: 'quotes.show', params: { id: String(quote.id) } }))
}

function abrirEditor(quote: Quote): void {
  void router.push(filtros.conQuery({ name: 'quotes.edit', params: { id: String(quote.id) } }))
}

function clearFilters(): void {
  filtros.limpiar()
  page.value = 1
  void load()
}
</script>

<template>
  <div class="space-y-6 animate-fade-in">
    <header class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <h1 class="text-2xl font-semibold text-surface-900 dark:text-surface-100">
          Cotizaciones
        </h1>
      </div>
      <Button
        v-if="canManage"
        label="Nueva cotización"
        icon="pi pi-plus"
        @click="router.push({ name: 'quotes.create' })"
      />
    </header>

    <AppCard>
      <template #header>
        <Accordion class="w-full" styleClass="w-full">
          <AccordionTab
            headerClass="flex items-center justify-between cursor-pointer hover:bg-surface-200 dark:hover:bg-surface-700 transition-colors"
            headerStyleClass="bg-surface-100 dark:bg-surface-800 rounded-lg border border-surface-200 dark:border-surface-700 shadow-sm"
            contentStyleClass="p-3 gap-3"
          >
            <template #header>
              <div class="flex items-center justify-between w-full py-2">
                <div class="flex items-center gap-2">
                  <i class="pi pi-filter text-primary-600 dark:text-primary-400" />
                  <span class="font-medium text-surface-900 dark:text-surface-100">
                    Filtros
                  </span>
                  <span
                    v-if="filtros.alguno"
                    class="inline-flex items-center justify-center px-2 py-0.5 text-xs font-medium bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300 rounded-full"
                  >
                    {{ filtrosContador }}
                  </span>
                </div>
                <i class="pi pi-chevron-down text-surface-500 dark:text-surface-400 transition-transform duration-200 text-lg" />
              </div>
            </template>

            <div class="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3" role="search">
              <InputText
                v-model="search"
                placeholder="Buscar por cliente, documento o factura"
                aria-label="Buscar en las cotizaciones"
                class="w-full lg:col-span-2"
              />
              <Select
                v-model="status"
                :options="statusOptions"
                option-label="label"
                option-value="value"
                placeholder="Estado"
                aria-label="Filtrar por estado"
                class="w-full"
              />
              <Select
                v-model="followUp"
                :options="followUpOptions"
                option-label="label"
                option-value="value"
                placeholder="Seguimiento"
                aria-label="Filtrar por seguimiento"
                class="w-full"
              />
              <Select
                v-model="priceType"
                :options="priceTypeOptions"
                option-label="label"
                option-value="value"
                placeholder="Tipo de precio"
                aria-label="Filtrar por tipo de precio"
                class="w-full"
              />
              <Select
                v-model="advisorId"
                :options="advisorOptions"
                option-label="label"
                option-value="value"
                placeholder="Asesor"
                aria-label="Filtrar por asesor"
                class="w-full"
              />
              <Select
                v-model="invoiced"
                placeholder="Facturación"
                :options="invoicedOptions"
                option-label="label"
                option-value="value"
                aria-label="Filtrar por facturación"
                class="w-full"
              />
              <Select
                v-model="advanceFilter"
                :options="advanceOptions"
                option-label="label"
                option-value="value"
                placeholder="Anticipos"
                aria-label="Filtrar por anticipos"
                class="w-full"
              />
              <Select
                v-model="attentionLineFilter"
                :options="attentionLineOptions"
                option-label="label"
                option-value="value"
                placeholder="Línea de atención"
                aria-label="Filtrar por línea de atención"
                class="w-full"
              />
              <!--
                Los dos campos del rango van juntos y **en horizontal**, también en
                móvil: en vertical hay que recorrer dos filas para leer un dato que
                es uno solo, y el patrón «desde … hasta» se lee de un vistazo solo si
                los dos extremos están a la vista el uno junto al otro.

                Cada `DateField` va **envuelto en un `div`**, y no suelto. `DateField`
                renderiza la etiqueta y el selector como dos nodos sin contenedor de
                por medio —un fragmento—, así que puesto directamente en la rejilla
                el bloque tenía cuatro hijos en lugar de dos: la etiqueta «Desde», su
                selector, la etiqueta «Hasta» y su selector. La rejilla los repartía
                en dos filas y el rango seguía viéndose apilado, con la etiqueta
                encima de la fecha equivocada al leerlo de un vistazo.

                El envoltorio convierte cada campo en una celda: la etiqueta queda
                sobre su propia fecha y las dos fechas quedan en la misma fila.

                El bloque ocupa toda la fila (`col-span`) en lugar de una celda: con
                una sola de las tres columnas de la rejilla exterior, cada fecha se
                quedaba con un tercio del ancho y el `dd/mm/aaaa` no cabía sin
                recortar.

                El `max` del primero y el `min` del segundo se cruzan para que el
                calendario no deje elegir un rango invertido. El backend también lo
                rechaza (`after_or_equal:date_from`), pero es mejor que el selector
                no llegue a dejar escribirlo.
              -->
              <div class="grid grid-cols-2 gap-3 sm:col-span-2 lg:col-span-3">
                <div>
                  <DateField
                    v-model="dateFrom"
                    input-id="filtro-quotes-desde"
                    label="Desde"
                    placeholder="dd/mm/aaaa"
                    :max="dateTo || undefined"
                  />
                </div>
                <div>
                  <DateField
                    v-model="dateTo"
                    input-id="filtro-quotes-hasta"
                    label="Hasta"
                    placeholder="dd/mm/aaaa"
                    :min="dateFrom || undefined"
                  />
                </div>
              </div>
            </div>
          </AccordionTab>
        </Accordion>

        <!--
          Qué filtros hay puestos. Al estar en la URL, el listado se puede compartir con
          los filtros ya puestos, y quien lo recibe ve exactamente la misma lista. Con la
          cuenta de filtros pegada encima se sabe qué está reduciendo el resultado sin
          tener que abrir los desplegables uno a uno.
        -->
        <div
          v-if="filtros.alguno"
          class="mt-3 flex flex-wrap items-center gap-1.5 text-xs text-surface-500 dark:text-surface-400"
          data-testid="filtros-activos"
        >
          <span>Filtros:</span>
          <Tag
            v-for="etiqueta in etiquetasDeFiltro()"
            :key="etiqueta.texto"
            :value="etiqueta.texto"
            severity="secondary"
          />
          <Button
            label="Quitar filtros"
            icon="pi pi-filter-slash"
            severity="secondary"
            outlined
            size="small"
            class="ml-2"
            @click="clearFilters"
          />
        </div>
      </template>

      <!-- Estado de error: separado del vacío para no mentir sobre la causa. -->
      <div
        v-if="errorMessage"
        class="flex flex-col items-center gap-3 py-12 text-center"
        role="alert"
      >
        <i class="pi pi-exclamation-triangle text-4xl text-amber-500" />
        <p class="font-medium text-surface-900 dark:text-surface-100">
          No se pudieron cargar las cotizaciones
        </p>
        <p class="max-w-md text-sm text-surface-500 dark:text-surface-400">
          {{ errorMessage }}
        </p>
        <Button
          label="Reintentar"
          icon="pi pi-refresh"
          severity="secondary"
          @click="load"
        />
      </div>

      <!-- Estado vacío -->
      <div
        v-else-if="isEmpty"
        class="flex flex-col items-center gap-3 py-12 text-center"
      >
        <i class="pi pi-inbox text-4xl text-surface-300 dark:text-surface-600" />
        <p class="font-medium text-surface-900 dark:text-surface-100">
          {{ hasFilters ? 'Sin resultados' : 'Todavía no hay cotizaciones' }}
        </p>
        <p class="max-w-md text-sm text-surface-500 dark:text-surface-400">
          {{
            hasFilters
              ? 'Prueba con otro término o quita los filtros aplicados.'
              : 'Crea la primera para empezar a vender.'
          }}
        </p>
        <Button
          v-if="hasFilters"
          label="Quitar filtros"
          severity="secondary"
          outlined
          @click="clearFilters"
        />
        <Button
          v-else-if="canManage"
          label="Crear la primera"
          icon="pi pi-plus"
          @click="router.push({ name: 'quotes.create' })"
        />
      </div>

      <!-- Estado con datos -->
      <template v-else>
        <DenseTable
          v-if="isDesktop"
          :columns="visibleColumns"
          :rows="rows"
          :loading="loading"
          layout="fixed"
          :sticky-right="columnasFijadas"
          @row-click="handleAction($event, 'view')"
          @action="handleAction"
        />

        <!--
          La tarjeta de móvil es **la misma lista** que la tabla, no otra versión con
          menos. La tabla solo se pinta a partir de 1024px, así que por debajo de eso esta
          tarjeta es lo único que hay: y le faltaban la facturación y las cuatro acciones
          de la fila. Es decir, en el móvil no se podía ver si algo estaba facturado ni
          facturarlo, editarlo o borrarlo sin abrir cada cotización una a una.

          Los datos van en una rejilla de dos columnas para que el total y la fecha no
          salten de lado, y las acciones al final, en una fila de iconos como en la tabla.
        -->
        <ul
          v-else
          class="divide-y divide-surface-200 dark:divide-surface-700"
          data-testid="quotes-mobile-list"
        >
          <li
            v-for="quote in items"
            :key="quote.id"
            class="py-3"
            :data-linea="quote.id"
          >
            <div class="flex items-start justify-between gap-3">
              <div class="min-w-0 flex-1">
                <div class="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <!--
                    El nombre se parte en varias lineas y no se recorta: un cliente
                    puede llamarse `Importadora y Distribuidora Nacional de
                    Suministros Industriales S.A.S.`, y con `truncate` en un movil
                    se leia `Importadora y Distribuidora N...`, que no permite
                    distinguirlo de otro cliente que empiece igual.
                  -->
                  <p class="min-w-0 break-words font-medium text-surface-900 dark:text-surface-100">
                    {{ quote.customer_name }}
                  </p>
                  <!--
                    La insignia va al lado del nombre y no en la rejilla de datos:
                    el dato que hay que ver de un vistazo es si esa cotización tiene
                    dinero adelantado, y arriba es donde se mira al recorrer. Con icono
                    de texto, no con color solo, porque el color no se lee con un
                    lector de pantalla.
                  -->
                  <span
                    v-if="quote.has_advances"
                    class="inline-flex shrink-0 items-center gap-1 rounded bg-emerald-100 px-1.5 py-0.5 text-xs font-medium text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200"
                    data-testid="quote-has-advance"
                  >
                    <i
                      class="pi pi-wallet text-xs"
                      aria-hidden="true"
                    />
                    Anticipo
                  </span>
                </div>
                <p
                  v-if="quote.customer_email"
                  class="break-words text-xs text-surface-500 dark:text-surface-400"
                >
                  {{ quote.customer_email }}
                </p>
              </div>
              <div class="flex items-center gap-2">
                <template v-if="canManage">
                  <Select
                    :modelValue="quote.status"
                    :options="estadosDeFila"
                    optionLabel="label"
                    optionValue="value"
                    :aria-label="`Estado de la cotización de ${quote.customer_name}`"
                    class="select-compacto w-auto min-w-[120px]"
                    @change="(evento: { value: QuoteStatus }) => pedirCambioDeEstado(quote, evento.value)"
                  >
                    <template #value="{ value }">
                      {{ statusLabel(value) }}
                    </template>
                  </Select>
                </template>
                <template v-else>
                  <Tag
                    :value="statusLabel(quote.status)"
                    :severity="statusSeverity(quote.status)"
                  />
                </template>
              </div>
            </div>

            <dl class="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
              <dt class="text-surface-500 dark:text-surface-400">
                Nº Cot.
              </dt>
              <dd class="text-right font-medium text-surface-900 dark:text-surface-100 font-mono">
                {{ quote.quote_number ?? '—' }}
              </dd>

              <dt class="text-surface-500 dark:text-surface-400">
                Total
              </dt>
              <dd class="text-right font-medium text-surface-900 dark:text-surface-100">
                {{ formatCurrency(quote.total_price) }}
              </dd>

              <dt class="text-surface-500 dark:text-surface-400">
                Productos
              </dt>
              <dd class="text-right text-surface-900 dark:text-surface-100">
                {{ quote.products_count }}
                {{ quote.products_count === 1 ? 'producto' : 'productos' }}
              </dd>

              <dt class="text-surface-500 dark:text-surface-400">
                Fecha
              </dt>
              <dd class="text-right text-surface-900 dark:text-surface-100">
                {{ formatShortDate(quote.quote_date) }}
              </dd>

              <dt class="text-surface-500 dark:text-surface-400">
                Tipo
              </dt>
              <dd class="text-right text-surface-900 dark:text-surface-100">
                <span
                  class="whitespace-nowrap rounded px-1.5 py-0.5 text-xs font-medium"
                  :class="quote.price_type === 'wholesale' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200' : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'"
                >
                  {{ quote.price_type === 'wholesale' ? 'Mayorista' : 'Detal' }}
                </span>
              </dd>

              <!--
                Lo que se pregunta al recorrer la lista: cuánto de esto ya está cobrado.
                El número va debajo, que es lo que se busca cuando ya se sabe cuál.
              -->
              <dt class="text-surface-500 dark:text-surface-400">
                Facturación
              </dt>
              <dd class="text-right">
                <span class="flex flex-col items-end gap-0.5">
                  <Tag
                    :value="esFacturada(quote) ? 'FACTURADA' : 'PENDIENTE'"
                    :severity="esFacturada(quote) ? 'success' : 'secondary'"
                  />
                  <span
                    v-if="esFacturada(quote) && quote.invoice_number"
                    class="font-mono text-xs text-surface-500 dark:text-surface-400"
                  >{{ quote.invoice_number }}</span>
                </span>
              </dd>

              <dt class="text-surface-500 dark:text-surface-400">
                Asesor
              </dt>
              <dd class="text-right">
                <!--
                  El nombre parte y no se recorta: la insignia iba con
                  `whitespace-nowrap` y con un asesor de nombre largo se salía de su
                  columna —5 px con «Alexander Corcho»— y con uno más largo habría
                  salido de la tarjeta. Al partirse, la insignia se queda dentro y
                  el texto se lee entero, que es la regla que ya se aplica al resto
                  de la lista.
                -->
                <span
                  class="inline-flex max-w-full items-center gap-1.5 rounded px-2 py-0.5 text-xs font-medium bg-violet-100 text-violet-800 dark:bg-violet-900 dark:text-violet-200"
                >
                  <i
                    class="pi pi-user shrink-0"
                    aria-hidden="true"
                  />
                  <span class="break-words">{{ quote.user_name }}</span>
                </span>
              </dd>

              <!--
                La línea va junto al asesor porque es lo otro que hay que saber al
                recorrer la lista: quién la lleva y por qué línea entra. En la tabla
                de escritorio estaba, pero esta tarjeta es **la misma lista** que la
                tabla, no una versión recortada: por debajo de 1024 px la tabla ni
                se pinta, así que sin esto el dato no se veía en móvil ni en tablet.

                Texto plano y no insignia: el color lo usa el estado, y un color más
                por fila acaba sin significar nada.
              -->
              <dt class="text-surface-500 dark:text-surface-400">
                Línea de atención
              </dt>
              <dd
                data-testid="quote-attention-line"
                class="text-right text-surface-900 dark:text-surface-100"
              >
                {{ quote.attention_line ?? '—' }}
              </dd>

              <dt
                v-if="quote.next_follow_up_date"
                class="text-surface-500 dark:text-surface-400"
              >
                Seguimiento
              </dt>
              <dd
                v-if="quote.next_follow_up_date"
                class="flex items-center justify-end gap-1.5 text-right text-surface-900 dark:text-surface-100"
              >
                <i
                  v-if="seguimientoVencido(quote.next_follow_up_date)"
                  class="pi pi-exclamation-circle text-xs text-red-500"
                  aria-hidden="true"
                />
                <span :class="seguimientoVencido(quote.next_follow_up_date) ? 'font-medium text-red-600 dark:text-red-400' : ''">
                  {{ formatShortDate(quote.next_follow_up_date) }}
                </span>
              </dd>
            </dl>

            <div
              v-if="quote.unread_notes_count > 0"
              class="mt-2 inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900 dark:text-amber-200"
            >
              <i class="pi pi-comment" />
              {{ quote.unread_notes_count }} nota(s) nueva(s)
            </div>

            <!--
              Las mismas cuatro acciones de la fila, con iconos. En móvil un botón con
              texto «Ver» por fila era lo único que había, y ocupa el ancho de la tarjeta
              para una sola cosa.
            -->
            <div class="mt-3 flex items-center gap-1">
              <Button
                icon="pi pi-eye"
                size="small"
                severity="secondary"
                rounded
                aria-label="Ver detalle"
                class="!size-11 !p-0"
                @click="abrirFicha(quote)"
              />
              <template v-if="canManage">
                <Button
                  icon="pi pi-pencil"
                  size="small"
                  severity="secondary"
                  rounded
                  aria-label="Editar"
                  class="!size-11 !p-0"
                  @click="abrirEditor(quote)"
                />
                <Button
                  v-if="!esFacturada(quote)"
                  icon="pi pi-receipt"
                  size="small"
                  severity="secondary"
                  rounded
                  aria-label="Marcar como facturada"
                  class="!size-11 !p-0"
                  @click="openInvoiceDialog(quote)"
                />
                <Button
                  icon="pi pi-trash"
                  size="small"
                  severity="danger"
                  rounded
                  aria-label="Borrar"
                  class="!size-11 !p-0"
                  @click="openDeleteDialog(quote)"
                />
              </template>
            </div>
          </li>
        </ul>

        <div
          v-if="total > perPage"
          class="flex flex-col items-center gap-3 border-t border-surface-200 pt-4 pb-safe sm:flex-row sm:justify-between dark:border-surface-700"
        >
          <p class="text-sm text-surface-500 dark:text-surface-400">
            {{ rangeLabel }}
          </p>
          <Paginator
            :first="(page - 1) * perPage"
            :rows="perPage"
            :total-records="total"
            template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
            @page="onPageChange"
          />
        </div>
      </template>
    </AppCard>

    <!--
      Facturar desde la fila.

      Pide el número porque una factura sin número no se puede auditar, y es la única
      forma de volver a mirar esa emisión más adelante. El botón de la fila no dispara
      nada por su cuenta: es un diálogo con un campo que hay que rellenar.
    -->
    <AppDialog
      v-model:visible="invoiceDialogVisible"
      :title="invoiceTarget ? `Marcar como facturada la de ${invoiceTarget.customer_name}` : 'Marcar como facturada'"
      confirm-label="Marcar como facturada"
      :closable="!rowActing"
      :close-on-escape="!rowActing"
      @confirm="markInvoiced"
      @cancel="invoiceDialogVisible = false"
    >
      <div class="space-y-2">
        <label
          for="invoice-number"
          class="block text-sm font-medium text-surface-700 dark:text-surface-300"
        >
          Número de factura <span class="text-red-500">*</span>
        </label>
        <InputText
          id="invoice-number"
          v-model="invoiceNumber"
          placeholder="FV-2026-0001"
          class="w-full"
        />
        <p class="text-sm text-surface-500 dark:text-surface-400">
          Una cotización facturada sin número no se puede auditar, así que es obligatorio.
        </p>
      </div>
    </AppDialog>

    <!--
      Borrar desde la fila.

      El texto dice de quién es la cotización y qué se va con ella. «¿Borrar?» a secas
      es el tipo de diálogo donde se pulsa «Sí» sin haber leído nada.
    -->
    <AppDialog
      ref="deleteDialog"
      v-model:visible="deleteDialogVisible"
      :title="deleteTarget ? `Borrar la cotización de ${deleteTarget.customer_name}` : 'Borrar cotización'"
      confirm-label="Borrar"
      :closable="!rowActing"
      :close-on-escape="!rowActing"
      @confirm="removeQuote"
      @cancel="deleteDialogVisible = false"
    >
      <p class="text-surface-700 dark:text-surface-300">
        Se va a borrar la cotización de
        <strong>{{ deleteTarget?.customer_name }}</strong>, con sus artículos y sus notas de
        seguimiento. Esta acción no se puede deshacer.
      </p>
    </AppDialog>

    <!--
      El diálogo de cambio de estado vive en el listado, no en la ficha. Con el cambio
      en los dos sitios, cada uno lleva su propia lista de estados y sus propias
      reglas, y se divergen.
    -->
    <AppDialog
      v-model:visible="statusDialogVisible"
      :title="statusTarget ? `Cambiar el estado de ${statusTarget.customer_name}` : 'Cambiar estado'"
      :closable="!statusChanging"
      :close-on-escape="!statusChanging"
      @confirm="applyStatus"
      @cancel="statusDialogVisible = false"
    >
      <div class="space-y-3">
        <Select
          v-model="newStatus"
          :options="statusOptions.filter((opcion) => opcion.value !== '')"
          option-label="label"
          option-value="value"
          placeholder="Estado"
          class="w-full"
          aria-label="Nuevo estado"
        />

        <Message
          v-if="statusError"
          severity="error"
          :closable="false"
          variant="simple"
          role="alert"
        >
          {{ statusError }}
        </Message>

        <p
          v-if="statusTarget && statusTarget.status === 'borrador'"
          class="text-sm text-surface-500 dark:text-surface-400"
        >
          Esta cotización está en borrador porque todavía no se ha guardado.
        </p>
      </div>
    </AppDialog>
  </div>
</template>
